import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Plane, 
  Umbrella, 
  Anchor, 
  Clock, 
  Star, 
  ArrowRight, 
  X, 
  CheckCircle, 
  Loader2 
} from "lucide-react";
import { Footer } from "../components/layout/Footer";
import { useLanguage } from "../context/LanguageContext";
import { API } from "../lib/api";

export default function UpsellsPage() {
  const [upsells, setUpsells] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedUpsell, setSelectedUpsell] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    preferred_date: "",
    preferred_time: "",
    num_guests: 1,
    notes: ""
  });
  const [bookingStatus, setBookingStatus] = useState({ loading: false, success: false, error: null, bookingId: null });
  const { t } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/upsells`).then(res => setUpsells(res.data)).catch(console.error);
  }, []);

  const categories = [
    { id: "all", label: t('all') },
    { id: "transfer", label: t('transfers') },
    { id: "beach", label: t('beach') },
    { id: "experience", label: t('experiences') },
    { id: "accommodation", label: t('stay') }
  ];

  const filteredUpsells = selectedCategory === "all" ? upsells : upsells.filter(u => u.category === selectedCategory);

  const getCategoryIcon = (category) => {
    const icons = { transfer: Plane, beach: Umbrella, experience: Anchor, accommodation: Clock };
    return icons[category] || Star;
  };

  // Generate descriptive alt text for upsell images
  const getUpsellAltText = (upsell) => {
    return `${upsell.name}: ${upsell.tagline || upsell.description?.substring(0, 50)} - €${upsell.price}`;
  };

  const getPackageId = (upsellId) => {
    const packageMap = {
      "1": "airport_transfer",
      "2": "beach_club",
      "3": "late_checkout",
      "4": "boat_tour",
      "5": "cooking_class",
      "6": "wine_tasting"
    };
    return packageMap[upsellId] || "airport_transfer";
  };

  const openBookingModal = (upsell) => {
    setSelectedUpsell(upsell);
    setShowBookingModal(true);
    setBookingStatus({ loading: false, success: false, error: null, bookingId: null });
    setBookingForm({
      guest_name: "",
      guest_email: "",
      guest_phone: "",
      preferred_date: "",
      preferred_time: "",
      num_guests: 1,
      notes: ""
    });
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedUpsell(null);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setBookingStatus({ loading: true, success: false, error: null, bookingId: null });

    try {
      const response = await axios.post(`${API}/upsells/book`, {
        package_id: getPackageId(selectedUpsell.id),
        guest_name: bookingForm.guest_name,
        guest_email: bookingForm.guest_email,
        guest_phone: bookingForm.guest_phone || null,
        preferred_date: bookingForm.preferred_date,
        preferred_time: bookingForm.preferred_time || null,
        num_guests: bookingForm.num_guests,
        notes: bookingForm.notes || null
      });

      setBookingStatus({ 
        loading: false, 
        success: true, 
        error: null, 
        bookingId: response.data.booking_id 
      });
    } catch (err) {
      setBookingStatus({ 
        loading: false, 
        success: false, 
        error: err.response?.data?.detail || "Failed to submit booking. Please try again.", 
        bookingId: null 
      });
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="upsells-page">
      <header className="bg-teal-700 text-white px-6 pt-8 pb-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-serif">{t('enhanceYourStayTitle')}</h1>
          <p className="text-teal-200 text-sm mt-2">{t('transfersBeachExperiences')}</p>
        </div>
      </header>

      <nav 
        className="px-6 py-4 max-w-4xl mx-auto flex gap-2 overflow-x-auto scrollbar-hide sticky top-[57px] bg-cream z-10"
        aria-label="Upsell categories"
      >
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              selectedCategory === cat.id ? "bg-teal-700 text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-teal-400"
            }`}
            aria-pressed={selectedCategory === cat.id}
          >
            {cat.label}
          </button>
        ))}
      </nav>

      <div className="px-6 py-6 max-w-4xl mx-auto space-y-4 pb-24 md:pb-8">
        {filteredUpsells.map((upsell) => {
          const Icon = getCategoryIcon(upsell.category);
          return (
            <article 
              key={upsell.id} 
              className="bg-white rounded-2xl overflow-hidden border border-stone-100 shadow-sm" 
              data-testid={`upsell-card-${upsell.id}`}
              aria-labelledby={`upsell-name-${upsell.id}`}
            >
              {upsell.image_url && (
                <img 
                  src={upsell.image_url} 
                  alt={getUpsellAltText(upsell)}
                  className="w-full h-44 object-cover"
                  loading="lazy"
                />
              )}
              <div className="p-5">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Icon size={18} className="text-teal-700" aria-hidden="true" />
                      <h2 id={`upsell-name-${upsell.id}`} className="font-serif text-xl text-stone-800">
                        {upsell.name}
                      </h2>
                    </div>
                    <p className="text-teal-700 font-medium text-sm mt-1">{upsell.tagline}</p>
                    <p className="text-[15px] text-stone-600 mt-2">{upsell.description}</p>
                  </div>
                  <p className="text-2xl font-bold text-teal-700 ml-4" aria-label={`Price: ${upsell.currency}${upsell.price}`}>
                    {upsell.currency}{upsell.price}
                  </p>
                </div>
                <button 
                  onClick={() => openBookingModal(upsell)}
                  className="mt-5 w-full bg-teal-700 hover:bg-teal-800 text-white py-3.5 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                  data-testid={`book-btn-${upsell.id}`}
                  aria-label={`${upsell.cta_text} - ${upsell.name} for ${upsell.currency}${upsell.price}`}
                >
                  {upsell.cta_text} <ArrowRight size={16} aria-hidden="true" />
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedUpsell && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" 
          onClick={closeBookingModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-modal-title"
        >
          <div 
            className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-stone-100">
              <div className="flex justify-between items-start">
                <div>
                  <h2 id="booking-modal-title" className="text-xl font-serif text-stone-900">
                    {t('bookNow')} {selectedUpsell.name}
                  </h2>
                  <p className="text-teal-700 font-semibold mt-1">{selectedUpsell.currency}{selectedUpsell.price}</p>
                </div>
                <button 
                  onClick={closeBookingModal} 
                  className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                  aria-label={t('close')}
                >
                  <X size={20} className="text-stone-500" aria-hidden="true" />
                </button>
              </div>
            </div>

            {bookingStatus.success ? (
              <div className="p-6 text-center" role="status">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-emerald-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold text-stone-900">{t('bookingRequested')}</h3>
                <p className="text-stone-600 mt-2">
                  {t('bookingReference')} <span className="font-mono font-bold text-teal-700">{bookingStatus.bookingId}</span>
                </p>
                <p className="text-stone-500 text-sm mt-3">
                  {t('confirmationSoon')}
                </p>
                <button 
                  onClick={closeBookingModal}
                  className="mt-6 w-full bg-teal-700 hover:bg-teal-800 text-white py-3 rounded-xl font-medium transition-colors"
                >
                  {t('done')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleBookingSubmit} className="p-6 space-y-4">
                <div>
                  <label htmlFor="guest_name" className="block text-sm font-medium text-stone-700 mb-1">
                    {t('yourName')} *
                  </label>
                  <input
                    id="guest_name"
                    type="text"
                    required
                    value={bookingForm.guest_name}
                    onChange={(e) => setBookingForm({...bookingForm, guest_name: e.target.value})}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="guest_email" className="block text-sm font-medium text-stone-700 mb-1">
                    {t('email')} *
                  </label>
                  <input
                    id="guest_email"
                    type="email"
                    required
                    value={bookingForm.guest_email}
                    onChange={(e) => setBookingForm({...bookingForm, guest_email: e.target.value})}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="guest_phone" className="block text-sm font-medium text-stone-700 mb-1">
                    {t('phone')} ({t('optional')})
                  </label>
                  <input
                    id="guest_phone"
                    type="tel"
                    value={bookingForm.guest_phone}
                    onChange={(e) => setBookingForm({...bookingForm, guest_phone: e.target.value})}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                    placeholder="+39 xxx xxx xxxx"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="preferred_date" className="block text-sm font-medium text-stone-700 mb-1">
                      {t('preferredDate')} *
                    </label>
                    <input
                      id="preferred_date"
                      type="date"
                      required
                      value={bookingForm.preferred_date}
                      onChange={(e) => setBookingForm({...bookingForm, preferred_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="preferred_time" className="block text-sm font-medium text-stone-700 mb-1">
                      {t('time')} ({t('optional')})
                    </label>
                    <input
                      id="preferred_time"
                      type="time"
                      value={bookingForm.preferred_time}
                      onChange={(e) => setBookingForm({...bookingForm, preferred_time: e.target.value})}
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="num_guests" className="block text-sm font-medium text-stone-700 mb-1">
                    {t('numberOfGuests')}
                  </label>
                  <select
                    id="num_guests"
                    value={bookingForm.num_guests}
                    onChange={(e) => setBookingForm({...bookingForm, num_guests: parseInt(e.target.value)})}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                  >
                    {[1,2,3,4,5,6,7,8].map(n => (
                      <option key={n} value={n}>{n} {n === 1 ? t('guest') : t('guests')}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-stone-700 mb-1">
                    {t('specialRequests')} ({t('optional')})
                  </label>
                  <textarea
                    id="notes"
                    value={bookingForm.notes}
                    onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                    rows={3}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 resize-none"
                    placeholder={t('anySpecialRequirements')}
                  />
                </div>

                {bookingStatus.error && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-xl text-sm" role="alert">
                    {bookingStatus.error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={bookingStatus.loading}
                  className="w-full bg-teal-700 hover:bg-teal-800 disabled:bg-stone-300 text-white py-3.5 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  {bookingStatus.loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                      {t('submitting')}
                    </>
                  ) : (
                    <>
                      {t('requestBooking')}
                      <ArrowRight size={16} aria-hidden="true" />
                    </>
                  )}
                </button>

                <p className="text-xs text-stone-500 text-center">
                  {t('confirmAvailability')}
                </p>
              </form>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
